/**
 * KB-META
 * Purpose: App menu builder
 * Role: ui.module
 * EntryPoints: 
 * Inputs: 
 * Outputs: 
 * Validation: 
 * Dependencies: 
 * Contracts: 
 * Auth&Privacy: auth:none; hipaa:none; pii:none
 * Observability: analytics.feedback; analytics.wallFeedback; analytics.wall
 * Router: shard=partner-portal-cura; H2=component/app.menu.builder; tags=ui
 * Owner: ; LastReviewed: 2025-08-25
 */
import { MenuItem } from 'primeng/api';
import { MENU, type MenuKey } from './menu.registry';
import { canSee } from './role.policy';
import { RoleLevel } from './roles';

const BUCKET_ICON: Record<string, string> = {
  'SETTING': 'pi pi-cog',
  'CONTENT': 'pi pi-th-large',
  'CONNECT': 'pi pi-share-alt',
  'TEAM': 'pi pi-users',
  'ANALYTICS': 'pi pi-chart-bar',
  'GENUSMEDIA': 'pi pi-video',
  'MANAGE ADMIN DATA': 'pi pi-database',
  'MANAGE REPORTS': 'pi pi-chart-bar',
  'DEV PLAYGROUND': 'pi pi-wrench'
};

function getExpandedBucketsForRoles(roles: RoleLevel[]): Set<string> {
  const buckets = new Set<string>();
  // Super Admin: everything collapsed (no additions)
  if (roles.includes(RoleLevel.CLINICIAN)) {
    buckets.add('CONNECT');
  }
  if (roles.includes(RoleLevel.CONTENT_MANAGER)) {
    buckets.add('CONTENT');
  }
  if (roles.includes(RoleLevel.PARTNER_ADMIN)) {
    buckets.add('SETTING');
    buckets.add('TEAM');
  }
  if (roles.includes(RoleLevel.SECURITY_ADMIN)) {
    buckets.add('TEAM');
  }
  if (roles.includes(RoleLevel.ANALYTICS_ADMIN)) {
    buckets.add('MANAGE REPORTS');
  }
  if (roles.includes(RoleLevel.SUPPORT_AGENT)) {
    buckets.add('MANAGE REPORTS');
  }
  if (roles.includes(RoleLevel.AUDITOR_READONLY)) {
    buckets.add('MANAGE REPORTS');
  }
  // If role is ALL, treat as super-admin-like but keep collapsed by default
  return buckets;
}

function byBucket(bucket: MenuItem['label'], keys: MenuKey[], roles: RoleLevel[]): MenuItem {
  const section: MenuItem & { expandedByDefault?: boolean } = {
    label: bucket,
    icon: BUCKET_ICON[String(bucket)] || 'pi pi-folder',
    items: keys.filter(k => canSee(k, roles)).map(k => ({
      label: MENU[k].label,
      icon: MENU[k].icon,
      routerLink: [MENU[k].route]
    }))
  } as any;
  // Mark the desired default expansion for root buckets based on role
  const expandedBuckets = getExpandedBucketsForRoles(roles);
  (section as any).expandedByDefault = expandedBuckets.has(String(bucket).toUpperCase());
  return section as MenuItem;
}

export function buildMenuForRoles(roles: RoleLevel[]): MenuItem[] {
  const sections: MenuItem[] = [
    byBucket('SETTING', [
      'settings.managePartner', 'settings.carecode', 'settings.modules',
      'settings.requestcode', 'settings.notifications', 'settings.careNudges'
    ], roles),
    byBucket('CONTENT', [
      'content.managePublicWall',
      'content.manageGenusTile', 'content.manageContactTile', 'content.managePathway',
      'content.questions', 'content.elements', 'content.mingle',
      'content.approveCrowdSource' // moved to bottom
    ], roles),
    byBucket('CONNECT', ['connect.careManagerDashboard','connect.manageCommunities','connect.campaigns','connect.shareInteraction','connect.placeNote','connect.coach'], roles),
    byBucket('TEAM', ['team.portalUsers','team.approvedMessageTeamOut','team.helpTips'], roles),
    byBucket('ANALYTICS', [
      'analytics.feedback','analytics.wallFeedback','analytics.wall','analytics.chat','analytics.cacheResponse'
    ], roles),
    byBucket('GENUSMEDIA', ['genusmedia.creator', 'genusmedia.companion', 'genusmedia.community-sense'], roles),
    byBucket('MANAGE ADMIN DATA', [
      'ops.procedures','ops.locations','ops.departments','ops.careTeams',
      'ops.referringSystems','ops.referringSystemTeams',
      'ops.physicianArtifacts','ops.requestedResources',
      'ops.customDates','ops.customNoteCategories','ops.medications','ops.hospitalServices',
      'ops.doctorReviewReport','ops.referringPhysicianReport','ops.patientDataTools'
    ], roles)
  ];
  // Dev Playground placed at bottom for Super Admin, Partner Admin, and ALL via policy
  sections.push(
    byBucket('DEV PLAYGROUND', [
      'dev.analyticsDashboard','dev.careManagerDashboard','dev.focusGroups','dev.patientSurveyTemplate','dev.patientWorkflow','dev.modalHealth','dev.genusAiTestChat','dev.apiSchemaTester','dev.technicalDocs','dev.analytics1','dev.analytics2','dev.radar1','dev.radar2','dev.wordFlip'
    ], roles)
  );
  // Add Manage Reports section with a nested "Portal Usage Analytics" submenu
  {
    const topKeys: MenuKey[] = [
      'reports.manage','reports.documents','reports.patientActivation','reports.doctorReviewPipeline','reports.refPhysicianDocs','reports.patientAttrition'
    ];
    const usageKeys: MenuKey[] = [
      'reports.portalUsageOverview',
      'reports.simpleSummary','reports.simpleUsageByUser','reports.simpleAudit','reports.simpleUserActivity','reports.workflowFunnel','reports.engagementHeatmap','reports.usageLeaderboard'
    ];
    // Add security report as part of usage analytics
    usageKeys.push('reports.securityPatientAccess');
    const topItems = topKeys.filter(k => canSee(k, roles)).map(k => ({
      label: MENU[k].label,
      icon: MENU[k].icon,
      routerLink: [MENU[k].route]
    }));
    const usageChildren = usageKeys.filter(k => canSee(k, roles)).map(k => ({
      label: MENU[k].label,
      icon: MENU[k].icon,
      routerLink: [MENU[k].route]
    }));
    const usageParent: MenuItem | null = usageChildren.length ? {
      label: 'Portal Usage Analytics',
      icon: 'pi pi-chart-line',
      routerLink: ['/analytics/portal-usage-analytics'],
      items: usageChildren
    } : null;

    const manageReports: MenuItem = {
      label: 'MANAGE REPORTS',
      icon: BUCKET_ICON['MANAGE REPORTS'],
      items: usageParent ? [...topItems, usageParent] : topItems
    } as MenuItem;
    if (manageReports.items && manageReports.items.length) sections.push(manageReports);
  }
  return sections.filter(section => (section.items && section.items.length) as any);
}


